/**
 * CurrentlyOnlineWidget · v2.7.65+E141
 * ────────────────────────────────────
 * "Who's watching dispatch right now" widget. Renders an overlapping
 * avatar stack + live count in the sidebar; expands into a full roster
 * popover with each teammate's 7-state presence dot (Online / Away /
 * On Break / Meeting / On Call / DND / Vacation / Offline).
 *
 * Data flow
 * ─────────
 *   useAdminPresence()  →  { [username]: { status, presence, ... } }
 *
 * `status` is the heartbeat-derived flag (fresh ping < 90 s). `presence`
 * is the operator-selectable 7-state from the sidebar footer. We show
 * everyone with `status === "online"` and colour each dot by their
 * 7-state `presence` so a dispatcher can see at a glance who's on a
 * call, DND, in a meeting, etc.
 */
import { useMemo, useState } from "react";
import useAdminPresence from "@/hooks/useAdminPresence";
import { PRESENCE_OPTIONS } from "@/components/admin/SidebarProfileFooter";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

// v2.7.65+E141 · Lookup table used by the widget rows + avatar dots.
const PRESENCE_BY_KEY = Object.fromEntries(PRESENCE_OPTIONS.map((p) => [p.key, p]));
const DEFAULT_PRESENCE = PRESENCE_BY_KEY.online;

function toUrl(maybeRelative) {
  if (!maybeRelative) return "";
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  return `${BASE}${maybeRelative}`;
}

function initialsOf(fn, ln, fallback) {
  const a = (fn || "").trim();
  const b = (ln || "").trim();
  if (a && b) return (a[0] + b[0]).toUpperCase();
  if (a) return a[0].toUpperCase();
  if (b) return b[0].toUpperCase();
  return (fallback || "?").slice(0, 1).toUpperCase();
}

function presenceMetaOf(entry) {
  const key = entry?.presence || (entry?.status === "online" ? "online" : "offline");
  return PRESENCE_BY_KEY[key] || DEFAULT_PRESENCE;
}


export default function CurrentlyOnlineWidget() {
  const presence = useAdminPresence();
  const [open, setOpen] = useState(false);

  const onlineList = useMemo(() => {
    const out = [];
    for (const [username, entry] of Object.entries(presence || {})) {
      if (entry?.status === "online") {
        out.push({
          username,
          first_name:   entry.first_name || "",
          last_name:    entry.last_name  || "",
          avatar_url:   entry.avatar_url || "",
          last_seen_at: entry.last_seen_at,
          presence:     entry.presence || "online",
          presence_note: entry.presence_note || "",
        });
      }
    }
    // Sort: available first (online), then busy, then away/vacation
    const order = { online: 0, away: 1, on_break: 1, meeting: 2, on_call: 2, dnd: 3, vacation: 4, offline: 5 };
    out.sort((a, b) => (order[a.presence] ?? 9) - (order[b.presence] ?? 9));
    return out;
  }, [presence]);

  const visible = onlineList.slice(0, 4);
  const overflow = Math.max(0, onlineList.length - visible.length);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md border border-zinc-800 bg-zinc-950/60 hover:border-emerald-400/40 transition-colors group text-left"
          data-testid="currently-online-widget"
          title="Currently online staff"
        >
          <span
            className={`h-2 w-2 rounded-full flex-shrink-0 ${
              onlineList.length > 0 ? "bg-emerald-400 admin-status-pulse" : "bg-zinc-600"
            }`}
          />
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 group-hover:text-zinc-200 flex-1 min-w-0 truncate">
            Currently Online
          </span>
          <span
            className="text-[11px] font-bold text-emerald-400"
            data-testid="currently-online-count"
          >
            {onlineList.length}
          </span>

          {/* Avatar stack — each avatar carries its own 7-state presence dot */}
          {visible.length > 0 && (
            <div className="flex -space-x-1.5 ml-1">
              {visible.map((u) => {
                const meta = presenceMetaOf(u);
                return (
                  <div
                    key={u.username}
                    className="relative h-5 w-5 rounded-full border-2 border-zinc-950 overflow-hidden bg-zinc-800 flex items-center justify-center"
                    title={`${(u.first_name || u.username)} ${u.last_name || ""}`.trim() + ` · ${meta.label}`}
                    data-testid={`stack-${u.username}`}
                  >
                    {u.avatar_url ? (
                      <img src={toUrl(u.avatar_url)} alt="" className="h-full w-full object-cover"/>
                    ) : (
                      <span className="text-[8px] font-bold text-amber-300">
                        {initialsOf(u.first_name, u.last_name, u.username)}
                      </span>
                    )}
                    <span
                      className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-[1.5px] ring-zinc-950"
                      style={{ backgroundColor: meta.color }}
                      data-testid={`stack-dot-${u.username}`}
                    />
                  </div>
                );
              })}
              {overflow > 0 && (
                <div className="h-5 w-5 rounded-full border-2 border-zinc-950 bg-zinc-700 flex items-center justify-center text-[8px] font-bold text-zinc-200">
                  +{overflow}
                </div>
              )}
            </div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="bg-zinc-900 border border-amber-400/40 w-72 p-2"
        data-testid="currently-online-popover"
      >
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 px-1 pb-1 border-b border-zinc-800 mb-1 flex items-center justify-between">
          <span>{onlineList.length} watching dispatch</span>
          {/* v2.7.65+E141 · Compact legend so dispatchers can decode dots */}
          <div className="flex items-center gap-1.5" title="Presence legend">
            {[
              PRESENCE_BY_KEY.online, PRESENCE_BY_KEY.away, PRESENCE_BY_KEY.meeting, PRESENCE_BY_KEY.vacation,
            ].map((p) => (
              <span
                key={p.key}
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: p.color }}
                title={p.label}
              />
            ))}
          </div>
        </div>
        {onlineList.length === 0 ? (
          <div className="text-xs text-zinc-500 px-1 py-3 text-center">
            Nobody&apos;s online right now.
          </div>
        ) : (
          <div className="max-h-72 overflow-auto">
            {onlineList.map((u) => {
              const meta = presenceMetaOf(u);
              return (
                <div
                  key={u.username}
                  className="flex items-center gap-2 px-1 py-1.5 hover:bg-zinc-800/40 rounded"
                  data-testid={`online-row-${u.username}`}
                >
                  <div className="relative h-7 w-7 flex-shrink-0">
                    <div className="h-full w-full rounded-full overflow-hidden bg-zinc-800 flex items-center justify-center border border-zinc-700">
                      {u.avatar_url ? (
                        <img src={toUrl(u.avatar_url)} alt="" className="h-full w-full object-cover"/>
                      ) : (
                        <span className="text-[9px] font-bold text-amber-300">
                          {initialsOf(u.first_name, u.last_name, u.username)}
                        </span>
                      )}
                    </div>
                    <span
                      className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-zinc-900"
                      style={{ backgroundColor: meta.color }}
                      data-testid={`row-dot-${u.username}`}
                      title={meta.label}
                    />
                  </div>
                  <div className="min-w-0 flex-1 leading-tight">
                    <div className="text-xs font-bold text-zinc-100 truncate">
                      {(u.first_name + " " + u.last_name).trim() || u.username}
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate flex items-center gap-1.5">
                      <span
                        className="inline-block h-1.5 w-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: meta.color }}
                      />
                      <span className="font-semibold uppercase tracking-wide" data-testid={`row-status-${u.username}`}>
                        {meta.label}
                      </span>
                      {u.presence_note && (
                        <span className="italic text-zinc-500 truncate">· {u.presence_note}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
